'use client';

import { useEffect, useState } from 'react';

/**
 * 목차 스크롤스파이 — 현재 읽고 있는 h2 의 id 를 반환한다.
 *
 * scroll 리스너 + throttle 대신 IntersectionObserver 를 쓴다. 리스너가 없고
 * 레이아웃을 읽지 않아 더 싸고, 교차 판정을 브라우저가 해줘 더 정확하다.
 *
 * 활성 판정 = 교차 중인 heading 중 뷰포트 상단에 가장 가까운 것.
 * 교차 중인 게 하나도 없으면(긴 문단 중간) 직전 값을 유지해 목차가 비지 않게 한다.
 */
export const useActiveHeading = (ids: string[]): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // ids 배열은 렌더마다 새 참조라 문자열로 고정해 의존성을 안정화한다
  const idsKey = ids.join('|');

  useEffect(() => {
    const headingIds = idsKey ? idsKey.split('|') : [];
    if (headingIds.length === 0) {
      setActiveId(null);
      return;
    }

    const elements = headingIds
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    // 스크롤 전에도 목차가 비어 보이지 않게 첫 항목을 활성으로 시작한다.
    // 요청한 id 가 아니라 실제로 DOM 에 존재하는 요소의 id 를 써야 한다 —
    // 그렇지 않으면 앞쪽 id 가 매칭되지 않을 때 활성 표시가 실체 없는 항목을 가리킨다
    setActiveId(elements[0].id);

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        // 교차 중인 게 없으면 직전 활성 항목을 유지한다
        if (visible.length === 0) return;

        const id = visible[0].target.id;
        if (id) setActiveId(id);
      },
      {
        // 글로벌 헤더(약 120px)를 지난 지점을 "읽는 중"으로 본다.
        // 아래쪽을 크게 잘라 화면 상단 근처의 heading 만 후보가 되게 한다.
        rootMargin: '-120px 0px -70% 0px',
      },
    );

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [idsKey]);

  return activeId;
};
